// src/components/CalendarPage.jsx
// Unified Calendar — Quinn Calendar (Notion-native) as primary source.
// Microsoft Outlook events shown as overlay when OAuth is connected.
// Full month/week/list views. Create events inline.

import React, { useState, useEffect, useCallback } from 'react';
import './CalendarPage.css';
import { useProfile } from '../context/UserProfileContext';
import { apiFetch, startOAuthConnect } from '../lib/api';

// ── Constants ─────────────────────────────────────────────────────────────────
const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];
const DAY_ABBR    = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const EVENT_TYPE_COLORS = {
  // Sales — green
  'Sales Meeting':       { bg: '#16a34a', light: 'var(--dm-green-wash)', text: '#14532d' },
  // Internal prep for sales — light green
  'Internal Prep':       { bg: '#4ade80', light: '#f0fdf4', text: 'var(--dm-green-deep)' },
  // Vendor meetings — brown
  'Vendor Meeting':      { bg: '#92400e', light: 'var(--dm-amber-light)', text: '#78350f' },
  // Internal meetings — orange
  'Internal':            { bg: '#ea580c', light: '#fff7ed', text: '#7c2d12' },
  'Cadence Touchpoint':  { bg: 'var(--dm-amber)', light: 'var(--dm-amber-wash)', text: '#92400e' },
  // Conference / Slim's Blocks — purple
  'Conference':          { bg: 'var(--dm-purple-vivid)', light: 'var(--dm-purple-pale)', text: '#4c1d95' },
  'Email/LinkedIn':      { bg: 'var(--dm-purple-vivid)', light: 'var(--dm-purple-pale)', text: '#4c1d95' },
  // Health / Medical — red
  'Health':              { bg: 'var(--dm-red)', light: 'var(--dm-red-wash)', text: '#7f1d1d' },
  // Personal / Family / Kids — blue
  'Personal':            { bg: 'var(--dm-blue-vivid)', light: 'var(--dm-blue-pale)', text: '#1e3a8a' },
  'Family':              { bg: 'var(--dm-blue-mid)', light: 'var(--dm-blue-pale)', text: '#1e40af' },
  'Kids Event':          { bg: '#60a5fa', light: 'var(--dm-blue-pale)', text: '#1e40af' },
  // Travel — blue-teal
  'Travel':              { bg: 'var(--dm-cyan)', light: '#ecfeff', text: '#164e63' },
  'Vacation':            { bg: '#06b6d4', light: '#ecfeff', text: '#164e63' },
  // Discovery / prospect
  'Discovery':           { bg: '#f59e0b', light: 'var(--dm-amber-wash)', text: '#78350f' },
  // General meeting (fallback)
  'Meeting':             { bg: 'var(--dm-indigo-soft)', light: '#eef2ff', text: 'var(--dm-purple-deep)' },
  'Task Due':            { bg: '#f59e0b', light: 'var(--dm-amber-wash)', text: '#78350f' },
  'Birthday':            { bg: '#ec4899', light: '#fdf2f8', text: '#831843' },
  'Anniversary':         { bg: '#ec4899', light: '#fdf2f8', text: '#831843' },
  'Subscription Renewal':{ bg: '#f97316', light: '#fff7ed', text: '#7c2d12' },
  'Expense':             { bg: '#84cc16', light: '#f7fee7', text: '#365314' },
};

const EVENT_TYPES = Object.keys(EVENT_TYPE_COLORS);

function eventStyle(type) {
  return EVENT_TYPE_COLORS[type] || { bg: 'var(--dm-grey-600)', light: '#f9fafb', text: '#1f2937' };
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function toDateStr(d) {
  return d.toISOString().split('T')[0];
}

function parseLocalDate(dateStr) {
  if (!dateStr) return null;
  // Parse as local date to avoid UTC shift
  const [y, m, d] = dateStr.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth()    === b.getMonth()    &&
         a.getDate()     === b.getDate();
}

// Convert a local date+time string (from the UI, assumed Eastern) to UTC ISO string.
// User picks "3:00 PM" in Eastern — we must store as "19:00:00.000Z" (EDT=UTC-4).
//
// FIX v29.3: The previous implementation used new Date(`${dateStr}T${timeStr}:00`)
// which the JS engine interprets in the SERVER's local timezone (UTC on Vercel), not
// Eastern. This produced times that were 4 hours wrong. The correct approach is to
// probe the Eastern UTC offset for the specific date (handles DST automatically),
// then add it to the user's selected hour.
function toUTCIso(dateStr, timeStr, tz = 'America/New_York') {
  if (!dateStr || !timeStr) return dateStr;
  // Probe the UTC offset for Eastern on this specific date.
  // 12:00 UTC is always within the same calendar day in Eastern, so it's a safe probe point.
  const probe = new Date(dateStr + 'T12:00:00Z');
  const easternNoonHour = parseInt(
    probe.toLocaleString('en-US', { hour: 'numeric', hour12: false, timeZone: tz }), 10
  );
  const utcOffsetHours = 12 - easternNoonHour; // 4 = EDT (UTC-4), 5 = EST (UTC-5)
  const [hours, minutes] = timeStr.split(':').map(Number);
  const utcHours = hours + utcOffsetHours;
  // Handle day rollover (e.g. 11 PM + 5h offset = 4 AM next day)
  if (utcHours >= 24) {
    const nextDay = new Date(dateStr + 'T00:00:00Z');
    nextDay.setUTCDate(nextDay.getUTCDate() + 1);
    const nextDateStr = nextDay.toISOString().split('T')[0];
    return nextDateStr + 'T' + String(utcHours - 24).padStart(2, '0') + ':' + String(minutes).padStart(2, '0') + ':00.000Z';
  }
  return dateStr + 'T' + String(utcHours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0') + ':00.000Z';
}

function formatTime(dateStr, tz = 'America/New_York') {
  if (!dateStr || !dateStr.includes('T')) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz });
}

function formatDateLabel(dateStr) {
  const d = parseLocalDate(dateStr);
  if (!d) return '';
  const today    = new Date(); today.setHours(0,0,0,0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  if (isSameDay(d, today))    return 'Today';
  if (isSameDay(d, tomorrow)) return 'Tomorrow';
  const diff = Math.round((d - today) / 86400000);
  if (diff > 0 && diff < 7) return DAY_ABBR[d.getDay()];
  return `${MONTH_NAMES[d.getMonth()].slice(0,3)} ${d.getDate()}`;
}

// ── Event pill (used in month grid) ──────────────────────────────────────────
function EventPill({ event, onClick }) {
  const style = eventStyle(event.eventType);
  const isPrivate = event.isPrivate;
  return (
    <div
      className="cal-pill"
      style={{ background: style.bg, color: 'var(--dm-white)' }}
      onClick={e => { e.stopPropagation(); onClick(event); }}
      title={event.eventName}
    >
      {isPrivate && '🔒 '}{event.eventName}
    </div>
  );
}

// ── Event detail card ─────────────────────────────────────────────────────────
function EventDetail({ event, onClose, onMarkDone, onEdit, onDelete }) {
  const style = eventStyle(event.eventType);
  const isOutlook = event.source === 'outlook';
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const { timezone: userTimezone = 'America/New_York' } = useProfile();

  return (
    <div className="cal-detail-overlay" onClick={onClose}>
      <div className="cal-detail-card" onClick={e => e.stopPropagation()}>
        <div className="cal-detail-header" style={{ borderLeftColor: style.bg }}>
          <div className="cal-detail-type-badge" style={{ background: style.light, color: style.text }}>
            {event.eventType || 'Event'}
            {event.isPrivate && ' 🔒'}
          </div>
          <button className="cal-detail-close" onClick={onClose}>✕</button>
        </div>
        <div className="cal-detail-title">{event.eventName || event.title}</div>
        <div className="cal-detail-meta">
          <span>📅 {formatDateLabel(event.date || event.start)}</span>
          {(event.date || event.start) && (event.date || event.start).includes('T') && (
            <span>⏰ {formatTime(event.date || event.start, userTimezone)}</span>
          )}
          {event.priority && event.priority !== 'Normal' && (
            <span className={`cal-priority-badge cal-priority-${event.priority.toLowerCase()}`}>
              {event.priority}
            </span>
          )}
        </div>
        {event.contactName && (
          <div className="cal-detail-row"><span className="cal-detail-label">Contact</span>{event.contactName}</div>
        )}
        {event.accountName && (
          <div className="cal-detail-row"><span className="cal-detail-label">Account</span>{event.accountName}</div>
        )}
        {event.recurrence && event.recurrence !== 'One-time' && (
          <div className="cal-detail-row"><span className="cal-detail-label">Recurrence</span>{event.recurrence}</div>
        )}
        {event.notes && (
          <div className="cal-detail-notes">{event.notes}</div>
        )}
        {/* Outlook-specific */}
        {event.organizer && (
          <div className="cal-detail-row"><span className="cal-detail-label">Organizer</span>{event.organizer}</div>
        )}
        {event.attendees?.length > 0 && (
          <div className="cal-detail-row">
            <span className="cal-detail-label">Attendees</span>
            <span>{event.attendees.map(a => a.name || a.email).join(', ')}</span>
          </div>
        )}
        {event.joinUrl && (
          <a className="cal-join-btn" href={event.joinUrl} target="_blank" rel="noreferrer">
            🎥 Join Meeting
          </a>
        )}
        {!isOutlook && (
          <div className="cal-detail-actions">
            {event.status !== 'Done' && (
              <button className="cal-done-btn" onClick={() => onMarkDone(event.id)}>✓ Done</button>
            )}
            <button className="cal-edit-btn" onClick={() => onEdit(event)}>✏️ Edit</button>
            {!confirmDelete ? (
              <button className="cal-delete-btn" onClick={() => setConfirmDelete(true)}>🗑 Delete</button>
            ) : (
              <div className="cal-delete-confirm">
                <span>Delete this event?</span>
                <button className="cal-delete-confirm-yes" onClick={() => onDelete(event.id)}>Yes, delete</button>
                <button className="cal-delete-confirm-no" onClick={() => setConfirmDelete(false)}>Cancel</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Edit Event modal ─────────────────────────────────────────────────────────
function EditEventModal({ event, onClose, onSave, userTimezone = 'America/New_York' }) {
  const parseTimeFromDate = (dateStr) => {
    if (!dateStr || !dateStr.includes('T')) return '';
    const d = new Date(dateStr);
    const local = d.toLocaleTimeString('en-US', {
      timeZone: userTimezone, hour: '2-digit', minute: '2-digit', hour12: false
    });
    return local.slice(0, 5); // HH:MM
  };
  const parseDateOnly = (dateStr) => dateStr ? dateStr.split('T')[0] : '';
  const getDurationMinutes = (start, end) => {
    if (!start || !end || !start.includes('T')) return 60;
    const diff = (new Date(end) - new Date(start)) / 60000;
    return diff > 0 ? diff : 60;
  };

  const [form, setForm] = React.useState({
    eventName:   event.eventName || '',
    eventType:   event.eventType || 'Meeting',
    date:        parseDateOnly(event.date),
    time:        parseTimeFromDate(event.date),
    duration:    getDurationMinutes(event.date, event.endDate),
    notes:       event.notes || '',
    isPrivate:   event.isPrivate || false,
    priority:    event.priority || 'Normal',
    recurrence:  event.recurrence || 'One-time',
    contactName: event.contactName || '',
    status:      event.status || 'Upcoming',
  });
  const [saving, setSaving] = React.useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const endInfo = form.time && form.duration
    ? addMinutes(form.date, form.time, form.duration)
    : null;

  const handleSave = async () => {
    if (!form.eventName || !form.date) return;
    setSaving(true);
    const dateValue = form.time ? toUTCIso(form.date, form.time, userTimezone) : form.date;
    const endDateValue = endInfo ? toUTCIso(endInfo.endDate, endInfo.endTime, userTimezone) : null;
    await onSave({
      pageId: event.id,
      eventName: form.eventName,
      eventType: form.eventType,
      date: dateValue,
      endDate: endDateValue,
      recurrence: form.recurrence,
      priority: form.priority,
      contactName: form.contactName,
      isPrivate: form.isPrivate,
      notes: form.notes,
      status: form.status,
    });
    setSaving(false);
  };

  return (
    <div className="cal-modal-overlay" onClick={onClose}>
      <div className="cal-modal" onClick={e => e.stopPropagation()}>
        <div className="cal-modal-header">
          <span className="cal-modal-title">Edit Event</span>
          <button className="cal-detail-close" onClick={onClose}>✕</button>
        </div>

        <div className="cal-modal-body">
          <input
            className="cal-modal-input"
            placeholder="Event name *"
            value={form.eventName}
            onChange={e => set('eventName', e.target.value)}
            autoFocus
          />

          <div className="cal-modal-row">
            <select className="cal-modal-select" value={form.eventType} onChange={e => set('eventType', e.target.value)}>
              {EVENT_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
            <select className="cal-modal-select" value={form.priority} onChange={e => set('priority', e.target.value)}>
              <option>High</option><option>Normal</option><option>Low</option>
            </select>
          </div>

          <div className="cal-modal-row">
            <input
              className="cal-modal-input"
              type="date"
              value={form.date}
              onChange={e => set('date', e.target.value)}
            />
          </div>

          <div className="cal-modal-row">
            <div className="cal-modal-field-group">
              <label className="cal-modal-field-label">Start time</label>
              <select className="cal-modal-select" value={form.time} onChange={e => set('time', e.target.value)}>
                {TIME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="cal-modal-field-group">
              <label className="cal-modal-field-label">Duration</label>
              <select
                className="cal-modal-select"
                value={form.duration}
                onChange={e => set('duration', Number(e.target.value))}
                disabled={!form.time}
              >
                {DURATION_OPTIONS.map(o => <option key={o.minutes} value={o.minutes}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {endInfo && form.time && (
            <div className="cal-modal-end-time">
              Ends at {endInfo.endTime.replace(/^(\d+):(\d+)$/, (_, h, m) => {
                const hh = Number(h);
                return hh === 0 ? `12:${m} AM` : hh < 12 ? `${hh}:${m} AM` : hh === 12 ? `12:${m} PM` : `${hh-12}:${m} PM`;
              })}
            </div>
          )}

          <div className="cal-modal-row">
            <select className="cal-modal-select" value={form.recurrence} onChange={e => set('recurrence', e.target.value)}>
              <option>One-time</option><option>Daily</option><option>Weekly</option>
              <option>Bi-weekly</option><option>Monthly</option><option>Quarterly</option><option>Annual</option>
            </select>
            <select className="cal-modal-select" value={form.status} onChange={e => set('status', e.target.value)}>
              <option>Upcoming</option><option>Done</option><option>Skipped</option><option>Cancelled</option>
            </select>
          </div>

          <input
            className="cal-modal-input"
            placeholder="Contact name (optional)"
            value={form.contactName}
            onChange={e => set('contactName', e.target.value)}
          />

          <textarea
            className="cal-modal-textarea"
            placeholder="Notes (optional)"
            value={form.notes}
            onChange={e => set('notes', e.target.value)}
            rows={2}
          />

          <label className="cal-modal-check">
            <input type="checkbox" checked={form.isPrivate} onChange={e => set('isPrivate', e.target.checked)} />
            <span>Private — visible to Quinn only</span>
          </label>
        </div>

        <div className="cal-modal-footer">
          <button className="cal-modal-cancel" onClick={onClose}>Cancel</button>
          <button
            className="cal-modal-save"
            onClick={handleSave}
            disabled={saving || !form.eventName || !form.date}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Time helpers ──────────────────────────────────────────────────────────────
const TIME_OPTIONS = (() => {
  const opts = [{ label: 'No time (all day)', value: '' }];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      const label = h === 0 ? `12:${mm} AM`
        : h < 12  ? `${h}:${mm} AM`
        : h === 12 ? `12:${mm} PM`
        : `${h-12}:${mm} PM`;
      opts.push({ label, value: `${hh}:${mm}` });
    }
  }
  return opts;
})();

const DURATION_OPTIONS = [
  { label: '15 min',   minutes: 15  },
  { label: '30 min',   minutes: 30  },
  { label: '45 min',   minutes: 45  },
  { label: '1 hour',   minutes: 60  },
  { label: '1.5 hrs',  minutes: 90  },
  { label: '2 hours',  minutes: 120 },
  { label: '2.5 hrs',  minutes: 150 },
  { label: '3 hours',  minutes: 180 },
  { label: '4 hours',  minutes: 240 },
  { label: 'All day',  minutes: 0   },
];

function addMinutes(dateStr, timeStr, minutes) {
  if (!timeStr || !minutes) return null;
  const dt = new Date(`${dateStr}T${timeStr}:00`);
  dt.setMinutes(dt.getMinutes() + minutes);
  const hh = String(dt.getHours()).padStart(2, '0');
  const mm = String(dt.getMinutes()).padStart(2, '0');
  // If crosses midnight, return next day date + time
  const endDate = toDateStr(dt);
  return { endDate, endTime: `${hh}:${mm}` };
}

// ── Add Event modal ───────────────────────────────────────────────────────────
function AddEventModal({ onClose, onSave, defaultDate, defaultTime }) {
  const { timezone: userTimezone = 'America/New_York' } = useProfile();
  const [form, setForm] = useState({
    eventName: '',
    eventType: 'Meeting',
    date: defaultDate || toDateStr(new Date()),
    time: defaultTime || '09:00',
    duration: 60,
    notes: '',
    isPrivate: false,
    priority: 'Normal',
    recurrence: 'One-time',
    contactName: '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Compute end time label for display
  const endInfo = form.time && form.duration
    ? addMinutes(form.date, form.time, form.duration)
    : null;

  const handleSave = async () => {
    if (!form.eventName || !form.date) return;
    setSaving(true);
    const dateValue = form.time ? toUTCIso(form.date, form.time, userTimezone) : form.date;
    const endDateValue = endInfo ? toUTCIso(endInfo.endDate, endInfo.endTime, userTimezone) : null;
    await onSave({ ...form, date: dateValue, endDate: endDateValue });
    setSaving(false);
  };

  return (
    <div className="cal-modal-overlay" onClick={onClose}>
      <div className="cal-modal" onClick={e => e.stopPropagation()}>
        <div className="cal-modal-header">
          <span className="cal-modal-title">Add Event</span>
          <button className="cal-detail-close" onClick={onClose}>✕</button>
        </div>

        <div className="cal-modal-body">
          <input
            className="cal-modal-input"
            placeholder="Event name *"
            value={form.eventName}
            onChange={e => set('eventName', e.target.value)}
            autoFocus
          />

          <div className="cal-modal-row">
            <select className="cal-modal-select" value={form.eventType} onChange={e => set('eventType', e.target.value)}>
              {EVENT_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
            <select className="cal-modal-select" value={form.priority} onChange={e => set('priority', e.target.value)}>
              <option>High</option><option>Normal</option><option>Low</option>
            </select>
          </div>

          <div className="cal-modal-row">
            <input
              className="cal-modal-input"
              type="date"
              value={form.date}
              onChange={e => set('date', e.target.value)}
            />
          </div>

          <div className="cal-modal-row">
            <div className="cal-modal-field-group">
              <label className="cal-modal-field-label">Start time</label>
              <select className="cal-modal-select" value={form.time} onChange={e => set('time', e.target.value)}>
                {TIME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="cal-modal-field-group">
              <label className="cal-modal-field-label">Duration</label>
              <select
                className="cal-modal-select"
                value={form.duration}
                onChange={e => set('duration', Number(e.target.value))}
                disabled={!form.time}
              >
                {DURATION_OPTIONS.map(o => <option key={o.minutes} value={o.minutes}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {endInfo && form.time && (
            <div className="cal-modal-end-time">
              Ends at {endInfo.endTime.replace(/^(\d+):(\d+)$/, (_, h, m) => {
                const hh = Number(h);
                return hh === 0 ? `12:${m} AM` : hh < 12 ? `${hh}:${m} AM` : hh === 12 ? `12:${m} PM` : `${hh-12}:${m} PM`;
              })}
            </div>
          )}

          <div className="cal-modal-row">
            <select className="cal-modal-select" value={form.recurrence} onChange={e => set('recurrence', e.target.value)}>
              <option>One-time</option><option>Daily</option><option>Weekly</option>
              <option>Bi-weekly</option><option>Monthly</option><option>Quarterly</option><option>Annual</option>
            </select>
            <input
              className="cal-modal-input"
              placeholder="Contact name (optional)"
              value={form.contactName}
              onChange={e => set('contactName', e.target.value)}
            />
          </div>

          <textarea
            className="cal-modal-textarea"
            placeholder="Notes (optional)"
            value={form.notes}
            onChange={e => set('notes', e.target.value)}
            rows={2}
          />

          <label className="cal-modal-check">
            <input type="checkbox" checked={form.isPrivate} onChange={e => set('isPrivate', e.target.checked)} />
            <span>Private — visible to Quinn only</span>
          </label>
        </div>

        <div className="cal-modal-footer">
          <button className="cal-modal-cancel" onClick={onClose}>Cancel</button>
          <button
            className="cal-modal-save"
            onClick={handleSave}
            disabled={saving || !form.eventName || !form.date}
          >
            {saving ? 'Saving...' : 'Add Event'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Month grid ────────────────────────────────────────────────────────────────
function MonthView({ year, month, events, onDayClick, onEventClick }) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  // Pad to complete last row
  while (cells.length % 7 !== 0) cells.push(null);

  const eventsForDay = (day) => {
    if (!day) return [];
    const cellDate = new Date(year, month, day);
    return events.filter(e => {
      const eDate = parseLocalDate(e.date || e.start);
      return eDate && isSameDay(eDate, cellDate);
    }).slice(0, 3); // max 3 pills per cell
  };

  return (
    <div className="cal-month-grid">
      {DAY_ABBR.map(d => (
        <div key={d} className="cal-month-day-header">{d}</div>
      ))}
      {cells.map((day, i) => {
        const isToday = day && isSameDay(new Date(year, month, day), today);
        const dayEvents = eventsForDay(day);
        return (
          <div
            key={i}
            className={`cal-month-cell ${!day ? 'cal-cell-empty' : ''} ${isToday ? 'cal-cell-today' : ''}`}
            onClick={() => day && onDayClick(new Date(year, month, day))}
          >
            {day && (
              <>
                <span className="cal-cell-num">{day}</span>
                <div className="cal-cell-events">
                  {dayEvents.map((ev, ei) => (
                    <EventPill key={ei} event={ev} onClick={onEventClick} />
                  ))}
                  {eventsForDay(day).length > 3 && (
                    <span className="cal-cell-more">+{eventsForDay(day).length - 3} more</span>
                  )}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── List view ─────────────────────────────────────────────────────────────────
function ListView({ events, onEventClick }) {
  const { timezone: userTimezone = 'America/New_York' } = useProfile();
  const grouped = {};
  events.forEach(e => {
    const key = (e.date || e.start || '').split('T')[0];
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(e);
  });

  const sortedDays = Object.keys(grouped).sort();

  if (sortedDays.length === 0) {
    return (
      <div className="cal-empty-state">
        <div className="cal-empty-icon">📅</div>
        <div className="cal-empty-msg">No upcoming events.</div>
        <div className="cal-empty-hint">Add events using the + button above.</div>
      </div>
    );
  }

  return (
    <div className="cal-list-view">
      {sortedDays.map(day => {
        const label = formatDateLabel(day);
        const d = parseLocalDate(day);
        const today = new Date(); today.setHours(0,0,0,0);
        const isToday = d && isSameDay(d, today);
        return (
          <div key={day} className="cal-list-day">
            <div className={`cal-list-day-label ${isToday ? 'cal-list-today' : ''}`}>
              {isToday && <span className="cal-today-dot" />}
              {label}
              <span className="cal-list-full-date">
                {d ? `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}` : day}
              </span>
            </div>
            <div className="cal-list-events">
              {grouped[day].map((ev, i) => {
                const style = eventStyle(ev.eventType);
                return (
                  <div
                    key={i}
                    className="cal-list-event"
                    style={{ borderLeftColor: style.bg }}
                    onClick={() => onEventClick(ev)}
                  >
                    <div className="cal-list-event-time">
                      {(ev.date || ev.start) && (ev.date || ev.start).includes('T')
                        ? formatTime(ev.date || ev.start, userTimezone)
                        : <span className="cal-list-allday">All day</span>
                      }
                    </div>
                    <div className="cal-list-event-info">
                      <div className="cal-list-event-name">
                        {ev.isPrivate && '🔒 '}{ev.eventName || ev.title}
                        {ev.source === 'outlook' && <span className="cal-outlook-badge">Outlook</span>}
                      </div>
                      <div className="cal-list-event-meta">
                        <span className="cal-list-type-badge" style={{ background: style.light, color: style.text }}>
                          {ev.eventType || 'Event'}
                        </span>
                        {ev.accountName && <span className="cal-list-account">{ev.accountName}</span>}
                        {ev.contactName && <span className="cal-list-account">{ev.contactName}</span>}
                        {ev.priority === 'High' && <span className="cal-priority-badge cal-priority-high">High</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Day view ──────────────────────────────────────────────────────────────────
function DayView({ date, events, onEventClick, onTimeClick, onBack, tz = 'America/New_York' }) {
  const { timezone: userTimezone = 'America/New_York' } = useProfile();
  const today = new Date(); today.setHours(0,0,0,0);
  const isToday = isSameDay(date, today);
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const dayEvents = events.filter(e => {
    const eDate = parseLocalDate(e.date || e.start);
    return eDate && isSameDay(eDate, date);
  });

  const eventsForHour = (hour) => dayEvents.filter(e => {
    if (!(e.date || e.start)?.includes('T')) return hour === 8; // all-day at 8am slot
    const h = parseInt(new Date(e.date || e.start).toLocaleString('en-US', { hour: 'numeric', hour12: false, timeZone: userTimezone || 'America/New_York' }), 10);
    return h === hour;
  });

  const allDayEvents = dayEvents.filter(e => !(e.date || e.start)?.includes('T'));

  const dayLabel = `${['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][date.getDay()]}, ${MONTH_NAMES[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;

  return (
    <div className="cal-day-view">
      <div className="cal-day-header">
        <button className="cal-day-back" onClick={onBack}>‹ Back</button>
        <div className={`cal-day-title ${isToday ? 'cal-day-title-today' : ''}`}>{dayLabel}</div>
        <button className="cal-add-btn" onClick={() => onTimeClick(date, 9)}>+ Add Event</button>
      </div>

      {allDayEvents.length > 0 && (
        <div className="cal-day-allday-row">
          <div className="cal-day-time-label">All day</div>
          <div className="cal-day-allday-events">
            {allDayEvents.map((ev, i) => {
              const style = eventStyle(ev.eventType);
              return (
                <div key={i} className="cal-day-event" style={{ background: style.light, borderLeftColor: style.bg, color: style.text }}
                  onClick={() => onEventClick(ev)}>
                  <span className="cal-day-event-name">{ev.eventName}</span>
                  <span className="cal-day-event-type">{ev.eventType}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="cal-day-grid">
        {hours.map(hour => {
          const hEvents = eventsForHour(hour);
          const label = hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour-12} PM`;
          const isWorkHour = hour >= 7 && hour <= 19;
          return (
            <div key={hour} className={`cal-day-row ${isWorkHour ? '' : 'cal-day-row-offhours'}`}
              onClick={() => onTimeClick(date, hour)}>
              <div className="cal-day-time-label">{label}</div>
              <div className="cal-day-slot">
                {hEvents.map((ev, i) => {
                  const style = eventStyle(ev.eventType);
                  return (
                    <div key={i} className="cal-day-event" style={{ background: style.light, borderLeftColor: style.bg, color: style.text }}
                      onClick={e => { e.stopPropagation(); onEventClick(ev); }}>
                      <span className="cal-day-event-time">{formatTime(ev.date || ev.start, userTimezone)}</span>
                      <span className="cal-day-event-name">{ev.isPrivate && '🔒 '}{ev.eventName}</span>
                      {ev.accountName && <span className="cal-day-event-account">{ev.accountName}</span>}
                      <span className="cal-day-event-type" style={{ color: style.bg }}>{ev.eventType}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Week view ─────────────────────────────────────────────────────────────────
function WeekView({ events, onEventClick, onDayClick, weekOffset, setWeekOffset }) {
  const { timezone: userTimezone = 'America/New_York' } = useProfile();
  const today = new Date(); today.setHours(0,0,0,0);

  // Monday of the target week
  const startOfWeek = new Date(today);
  const dayOfWeek = today.getDay();
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  startOfWeek.setDate(today.getDate() + diff + (weekOffset * 7));

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    return d;
  });

  const eventsForDay = (date) => events.filter(e => {
    const eDate = parseLocalDate(e.date || e.start);
    return eDate && isSameDay(eDate, date);
  });

  const weekLabel = startOfWeek.getMonth() === endOfWeek.getMonth()
    ? `${MONTH_NAMES[startOfWeek.getMonth()]} ${startOfWeek.getDate()}–${endOfWeek.getDate()}, ${startOfWeek.getFullYear()}`
    : `${MONTH_NAMES[startOfWeek.getMonth()].slice(0,3)} ${startOfWeek.getDate()} – ${MONTH_NAMES[endOfWeek.getMonth()].slice(0,3)} ${endOfWeek.getDate()}, ${startOfWeek.getFullYear()}`;

  const DAY_ABBR2 = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

  return (
    <div className="cal-week-container">
      <div className="cal-week-nav">
        <button className="cal-nav-btn" onClick={() => setWeekOffset(w => w - 1)}>‹</button>
        <span className="cal-month-label">{weekLabel}</span>
        <button className="cal-nav-btn" onClick={() => setWeekOffset(w => w + 1)}>›</button>
        {weekOffset !== 0 && (
          <button className="cal-today-btn" onClick={() => setWeekOffset(0)}>Today</button>
        )}
      </div>
      <div className="cal-week-grid">
        {weekDays.map((date, i) => {
          const isToday = isSameDay(date, today);
          const dayEvents = eventsForDay(date);
          const isWeekend = i >= 5;
          return (
            <div key={i}
              className={`cal-week-col ${isToday ? 'cal-week-col-today' : ''} ${isWeekend ? 'cal-week-col-weekend' : ''}`}
              onClick={() => onDayClick(date)}
            >
              <div className={`cal-week-day-header ${isToday ? 'cal-week-today-header' : ''}`}>
                <span className="cal-week-day-abbr">{DAY_ABBR2[i]}</span>
                <span className={`cal-week-day-num ${isToday ? 'cal-week-today-num' : ''}`}>
                  {date.getDate()}
                </span>
                <span className="cal-week-month-hint">{MONTH_NAMES[date.getMonth()].slice(0,3)}</span>
              </div>
              <div className="cal-week-events">
                {dayEvents.length === 0 && <div className="cal-week-empty">—</div>}
                {dayEvents.map((ev, ei) => {
                  const style = eventStyle(ev.eventType);
                  return (
                    <div key={ei} className="cal-week-event"
                      style={{ background: style.light, borderLeftColor: style.bg, color: style.text }}
                      onClick={e => { e.stopPropagation(); onEventClick(ev); }}>
                      <div className="cal-week-event-time">
                        {(ev.date||ev.start)?.includes('T') ? formatTime(ev.date||ev.start, userTimezone) : 'All day'}
                      </div>
                      <div className="cal-week-event-name">
                        {ev.isPrivate && '🔒 '}{ev.eventName || ev.title}
                      </div>
                      <div className="cal-week-event-type" style={{ color: style.bg }}>{ev.eventType}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main CalendarPage ─────────────────────────────────────────────────────────
export default function CalendarPage({ onNavigate }) {
  const { timezone: userTimezone = 'America/New_York' } = useProfile();
  const today = new Date();
  const [view,         setView]         = useState('week'); // 'month' | 'week' | 'list' | 'day'
  const [year,         setYear]         = useState(today.getFullYear());
  const [month,        setMonth]        = useState(today.getMonth());
  const [weekOffset,   setWeekOffset]   = useState(0);
  const [dayViewDate,  setDayViewDate]  = useState(null);
  const [events,       setEvents]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [selectedEvent,setSelectedEvent]= useState(null);
  const [editEvent,    setEditEvent]    = useState(null);
  const [showAdd,      setShowAdd]      = useState(false);
  const [addDefaultDate,setAddDefaultDate] = useState(null);
  const [addDefaultTime,setAddDefaultTime] = useState(null);
  const [filter,       setFilter]       = useState('all'); // 'all' | 'business' | 'personal'
  // 'connected' | 'disconnected' | 'unsupported' (Google: no calendar scope) |
  // 'unavailable' (operator has not configured OAuth) | 'unknown'
  const [outlookStatus,setOutlookStatus]= useState('unknown');
  const [outlookReason,setOutlookReason]= useState('');
  const [outlookLabel, setOutlookLabel] = useState('');

  // ── Load events ─────────────────────────────────────────────────────────────
  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Date range: 1 month back + 4 months ahead to support week navigation
      const start = new Date(year, month - 1, 1);
      const end   = new Date(year, month + 4, 0);
      const startStr = toDateStr(start);
      const endStr   = toDateStr(end);

      // Fetch Quinn Calendar (Notion-native) — always primary
      const quinnRes = await apiFetch(`/api/quinn/calendar?start=${startStr}&end=${endStr}&includePrivate=true`
      );
      const quinnData = await quinnRes.json();
      let allEvents = (quinnData.events || [])
        .filter(e => e.status !== 'Cancelled')
        .map(e => ({ ...e, source: 'quinn' }));

      // Fetch AI Meetings DB — this is where real logged meetings live
      // (separate from Quinn Calendar DB which is for Quinn-created reminders/cadences)
      try {
        const meetingsRes = await apiFetch(`/api/meetings/list?start=${startStr}&end=${endStr}`);
        if (meetingsRes.ok) {
          const meetingsData = await meetingsRes.json();
          const meetingEvents = (meetingsData.meetings || []).map(m => ({
            id:        'meeting-' + m.id,
            eventName: m.title || m.name || 'Meeting',
            eventType: 'Meeting',
            date:      m.date || m.meetingDate || m.start,
            isPrivate: false,
            source:    'meetings',
            status:    'Upcoming',
            notes:     m.summary || m.notes || '',
            accountName: m.accountName || null,
          }));
          allEvents = [...allEvents, ...meetingEvents];
        }
      } catch { /* silent fail — meetings are supplemental */ }

      // Attempt Outlook overlay — silent fail if not connected
      try {
        const outlookRes = await apiFetch('/api/calendar/upcoming?days=90');
        if (outlookRes.ok) {
          const outlookData = await outlookRes.json();
          // `overlay` says WHY there is nothing to show, on a successful response.
          // A Google-only account is a known state, not a failure — it briefly
          // answered 501 and that put an amber row in the log dashboard on every
          // calendar visit, which is noise, not information.
          if (outlookData.overlay === 'unsupported') {
            setOutlookStatus('unsupported');
            // The server explains THIS connection — whether Google was granted a
            // calendar scope or not. Carrying the reason means the note can say why
            // instead of naming a product the user may not have.
            setOutlookReason(outlookData.reason || '');
            setOutlookLabel(outlookData.label || '');
          } else if (outlookData.success && outlookData.meetings) {
            setOutlookStatus('connected');
            const outlookEvents = outlookData.meetings.map(m => ({
              id:         'outlook-' + m.title + m.start,
              eventName:  m.title,
              eventType:  'Meeting',
              date:       m.start,
              endDate:    m.end,
              isPrivate:  false,
              source:     'outlook',
              joinUrl:    m.joinUrl,
              organizer:  m.organizer,
              attendees:  m.attendees,
              notes:      m.preview,
              status:     'Upcoming',
            }));
            allEvents = [...allEvents, ...outlookEvents];
          } else if (outlookRes.status === 401) {
            setOutlookStatus('disconnected');
          }
        } else {
          // READ THE CODE, not just "it failed". Every non-OK response used to become
          // "Connect Outlook", which is the wrong prompt for two of them: a 503 means
          // the OPERATOR has not set the env vars, and a 501 means this account is on
          // Google, whose token carries no calendar scope. Clicking Connect fixes
          // neither, and offering it hides the real fault.
          const detail = await outlookRes.json().catch(() => ({}));
          // PROVIDER_UNSUPPORTED is still handled here for safety, but the endpoint
          // no longer raises it — an unreadable provider now comes back as a 200
          // carrying overlay: 'unsupported', handled above.
          if (detail.code === 'PROVIDER_UNSUPPORTED') setOutlookStatus('unsupported');
          else if (detail.code === 'NOT_CONFIGURED')  setOutlookStatus('unavailable');
          else                                        setOutlookStatus('disconnected');
        }
      } catch { setOutlookStatus('disconnected'); }

      // Sort all events by date
      allEvents.sort((a, b) => {
        const da = a.date || a.start || '';
        const db = b.date || b.start || '';
        return da.localeCompare(db);
      });

      setEvents(allEvents);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // ── Filter events ────────────────────────────────────────────────────────────
  const visibleEvents = events.filter(e => {
    if (filter === 'business') return !e.isPrivate;
    if (filter === 'personal') return !!e.isPrivate;
    return true;
  }).filter(e => e.status !== 'Done' && e.status !== 'Cancelled');

  // For list view: only show from today forward
  const listEvents = view === 'list'
    ? visibleEvents.filter(e => {
        const d = parseLocalDate(e.date || e.start);
        if (!d) return false;
        const t = new Date(); t.setHours(0,0,0,0);
        return d >= t;
      })
    : visibleEvents;

  // ── Actions ──────────────────────────────────────────────────────────────────
  const handleAddEvent = async (formData) => {
    try {
      await apiFetch('/api/quinn/calendar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      setShowAdd(false);
      loadEvents();
    } catch (err) {
      console.error('Failed to add event:', err);
    }
  };

  const handleMarkDone = async (id) => {
    await apiFetch('/api/quinn/calendar', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pageId: id, status: 'Done' }),
    });
    setSelectedEvent(null);
    loadEvents();
  };

  const handleEditSave = async (updates) => {
    try {
      await apiFetch('/api/quinn/calendar', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      setEditEvent(null);
      setSelectedEvent(null);
      loadEvents();
    } catch (err) {
      console.error('Failed to update event:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiFetch('/api/quinn/calendar', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pageId: id }),
      });
      setSelectedEvent(null);
      loadEvents();
    } catch (err) {
      console.error('Failed to delete event:', err);
    }
  };

  const handleDayClick = (date) => {
    setDayViewDate(date);
    setView('day');
  };

  const handleTimeClick = (date, hour) => {
    const dateStr = toDateStr(date);
    // Round to nearest 15 min increment
    const hh = String(hour).padStart(2, '0');
    setAddDefaultDate(dateStr);
    setAddDefaultTime(`${hh}:00`);
    setShowAdd(true);
  };

  const navMonth = (dir) => {
    let m = month + dir;
    let y = year;
    if (m < 0)  { m = 11; y--; }
    if (m > 11) { m = 0;  y++; }
    setMonth(m);
    setYear(y);
  };

  // ── Counts for badges ────────────────────────────────────────────────────────
  const todayEvents = visibleEvents.filter(e => {
    const d = parseLocalDate(e.date || e.start);
    return d && isSameDay(d, today);
  });
  const highPriorityCount = visibleEvents.filter(e => e.priority === 'High').length;

  return (
    <div className="cal-page">

      {/* ── Header ── */}
      <div className="cal-header">
        <div className="cal-header-left">
          <div className="cal-title-row">
            <h1 className="cal-title">Calendar</h1>
            {todayEvents.length > 0 && (
              <span className="cal-today-badge">{todayEvents.length} today</span>
            )}
            {highPriorityCount > 0 && (
              <span className="cal-urgent-badge">⚠️ {highPriorityCount} urgent</span>
            )}
          </div>
          <div className="cal-subtitle-row">
            <span className="cal-subtitle">Quinn-managed</span>
            {outlookStatus === 'connected' && (
              <span className="cal-outlook-connected">+ Outlook synced</span>
            )}
            {outlookStatus === 'disconnected' && (
              <button type="button" className="cal-outlook-link"
                      onClick={() => startOAuthConnect('microsoft')}>
                Connect Outlook
              </button>
            )}
            {/* Not a button: neither of these is fixable by connecting Outlook, and
                offering the action anyway sends the user somewhere that cannot help. */}
            {outlookStatus === 'unsupported' && (
              // The reason comes from the server, which read THIS user's grant. The
              // first version said "Calendar overlay needs Microsoft 365" to a user
              // who had just connected Google — true, but it reads as though the app
              // had not noticed, and it names a product rather than explaining why.
              <span className="cal-outlook-note" title={outlookReason}>
                {outlookLabel || 'Calendar overlay unavailable'}
              </span>
            )}
            {outlookStatus === 'unavailable' && (
              <span className="cal-outlook-note" title="Microsoft OAuth is not configured on this deployment">
                Calendar overlay unavailable
              </span>
            )}
          </div>
        </div>

        <div className="cal-header-right">
          {/* Filter pills */}
          <div className="cal-filter-pills">
            {['all', 'business', 'personal'].map(f => (
              <button
                key={f}
                className={`cal-filter-pill ${filter === f ? 'active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? 'All' : f === 'business' ? '💼 Business' : '🏠 Personal'}
              </button>
            ))}
          </div>

          {/* View toggle */}
          <div className="cal-view-toggle">
            <button className={`cal-view-btn ${view === 'list' ? 'active' : ''}`} onClick={() => setView('list')}>
              ☰ List
            </button>
            <button className={`cal-view-btn ${view === 'day' ? 'active' : ''}`} onClick={() => { setDayViewDate(today); setView('day'); }}>
              ○ Day
            </button>
            <button className={`cal-view-btn ${view === 'week' ? 'active' : ''}`} onClick={() => setView('week')}>
              ▦ Week
            </button>
            <button className={`cal-view-btn ${view === 'month' ? 'active' : ''}`} onClick={() => setView('month')}>
              ⊞ Month
            </button>
          </div>

          <button className="cal-add-btn" onClick={() => { setAddDefaultDate(toDateStr(today)); setShowAdd(true); }}>
            + Add Event
          </button>
        </div>
      </div>

      {/* ── Month nav (month view only) ── */}
      {view === 'month' && (
        <div className="cal-month-nav">
          <button className="cal-nav-btn" onClick={() => navMonth(-1)}>‹</button>
          <span className="cal-month-label">{MONTH_NAMES[month]} {year}</span>
          <button className="cal-nav-btn" onClick={() => navMonth(1)}>›</button>
          <button className="cal-today-btn" onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth()); }}>
            Today
          </button>
        </div>
      )}

      {/* ── Body ── */}
      {loading && (
        <div className="cal-loading">
          <div className="cal-loading-dot" /><div className="cal-loading-dot" /><div className="cal-loading-dot" />
          <span>Loading calendar…</span>
        </div>
      )}

      {error && !loading && (
        <div className="cal-error-bar">⚠️ {error} <button onClick={loadEvents}>Retry</button></div>
      )}

      {!loading && !error && view === 'day' && dayViewDate && (
        <DayView
          date={dayViewDate}
          events={visibleEvents}
          onEventClick={setSelectedEvent}
          onTimeClick={handleTimeClick}
          onBack={() => setView('week')}
        />
      )}

      {!loading && !error && view === 'week' && (
        <WeekView
          events={visibleEvents}
          onEventClick={setSelectedEvent}
          onDayClick={handleDayClick}
          weekOffset={weekOffset}
          setWeekOffset={setWeekOffset}
        />
      )}

      {!loading && !error && view === 'month' && (
        <MonthView
          year={year}
          month={month}
          events={listEvents}
          onDayClick={handleDayClick}
          onEventClick={setSelectedEvent}
        />
      )}

      {!loading && !error && view === 'list' && (
        <ListView
          events={listEvents}
          onEventClick={setSelectedEvent}
        />
      )}

      {/* ── Modals ── */}
      {selectedEvent && !editEvent && (
        <EventDetail
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onMarkDone={handleMarkDone}
          onEdit={(ev) => { setEditEvent(ev); setSelectedEvent(null); }}
          onDelete={handleDelete}
        />
      )}

      {editEvent && (
        <EditEventModal
          event={editEvent}
          onClose={() => setEditEvent(null)}
          onSave={handleEditSave}
          userTimezone={userTimezone}
        />
      )}

      {showAdd && (
        <AddEventModal
          defaultDate={addDefaultDate}
          defaultTime={addDefaultTime}
          onClose={() => { setShowAdd(false); setAddDefaultTime(null); }}
          onSave={handleAddEvent}
        />
      )}
    </div>
  );
}
